/**
 * @module composables/useDashboardPermission
 * @description 대시보드 위젯의 권한(RBAC) 분기를 단일 진실 원천(single source)으로
 * 관리하는 composable. (SURINP-183 v2 ADDENDUM, v4 — 커스터마이즈/위젯 권한 완화)
 *
 * 역할:
 * - Vuex 인증 상태(`auth.user.id.userType`/`userRoleCode`)를 기반으로 ADMIN 여부 판별
 * - `adminOnly: true` 위젯을 일반 사용자에게서 자동 제거
 *   (현재 잔존: technician-calendar — 백엔드 강제 403)
 * - 풀 커스터마이징 패널 노출 권한(`canCustomize`) 제공
 *   v4부터는 일반 사용자도 자기 대시보드의 가시성/순서를 자유롭게 조정 가능
 *
 * 임시 정책 (SURINP-182 합의 시점까지):
 * - `userRoleCode === 'ADMIN'`을 ADMIN으로 간주 (기존 useFeeByUserType과 동일 패턴)
 * - 향후 `SOOP_ADMIN` / `SUPER_ADMIN` 등 enum 합의 시 ADMIN_ROLE_CODES 한 줄만 교체
 *
 * 보안 주의:
 * - 본 composable은 클라이언트 측 가시성만 제어한다. 백엔드 RBAC와 이중 검증해야 한다.
 *   (예: TechnicianCalendar API는 백엔드에서 ADMIN 외에는 403을 반환)
 * - SLA/Partner KPI/Region/Trend는 백엔드에서 userGroupId 필터로 자기 파트너만 응답.
 */
import { computed } from 'vue'
import store from '@/store'

/**
 * ADMIN으로 간주할 userRoleCode 목록.
 * SURINP-182 합의 시점까지 'ADMIN'만 사용. 합의 후 ['SOOP_ADMIN', 'SUPER_ADMIN'] 등으로 확장.
 */
const ADMIN_ROLE_CODES = Object.freeze(['ADMIN'])

/**
 * Vuex 스토어에서 현재 사용자의 역할 코드를 안전하게 읽어온다.
 * 비로그인 또는 user.id 미초기화 상태에서도 예외를 던지지 않는다.
 *
 * 우선순위: userType (로그인 JWT에서 추출, 프로젝트 전반의 표준 ADMIN 판별 키) →
 * userRoleCode (MyPage 방문 후 별도 commit되는 보조 필드).
 *
 * @returns {string|null} 역할 코드 또는 null
 */
function readUserRoleCode() {
  const id = store?.state?.auth?.user?.id
  if (!id || typeof id !== 'object') {
    return null
  }
  if (typeof id.userType === 'string' && id.userType.length > 0) {
    return id.userType
  }
  return typeof id.userRoleCode === 'string' ? id.userRoleCode : null
}

/**
 * 대시보드 권한(RBAC) 분기를 제공하는 composable.
 *
 * 반환값:
 * - isAdmin: ComputedRef<boolean> — userRoleCode가 ADMIN_ROLE_CODES에 포함되는지
 * - canCustomize: ComputedRef<boolean> — 풀 커스터마이징 패널 노출 가능 여부.
 *   v4부터 항상 true. LocalStorage는 사용자별로 분리되어 있어 일반 유저가 자기 대시보드만 영향.
 * - filterByPermission(widgets): adminOnly 위젯을 권한에 따라 필터링한 배열 반환
 *
 * @returns {{
 *   isAdmin: import('vue').ComputedRef<boolean>,
 *   canCustomize: import('vue').ComputedRef<boolean>,
 *   filterByPermission: <T extends {adminOnly?: boolean}>(widgets: ReadonlyArray<T>) => T[]
 * }}
 */
export function useDashboardPermission() {
  /**
   * 현재 사용자의 ADMIN 여부.
   * Vuex 상태는 store.state로 직접 접근하기에 reactivity가 필요하면 호출자가
   * computed/watch로 감싸야 한다. 본 composable은 호출 시점의 스냅샷 기반.
   * 단, computed로 감싸면 동일 컴포넌트 내 store dispatch 시 자동 반영된다.
   */
  const isAdmin = computed(() => {
    const code = readUserRoleCode()
    if (code === null) {
      return false
    }
    return ADMIN_ROLE_CODES.includes(code)
  })

  /**
   * 풀 커스터마이징 패널 노출 권한.
   * v4부터 일반 사용자도 자기 대시보드의 위젯 가시성/순서를 자유롭게 조정 가능.
   * LocalStorage는 사용자별로 분리되어 있어 다른 사용자에게 영향이 가지 않는다.
   * isAdmin과 별개의 reactive computed로 분리해두어 향후 별도 권한 분기 시 확장 용이.
   */
  const canCustomize = computed(() => true)

  /**
   * 위젯 목록에서 권한 미충족 위젯을 제거한다.
   * - widget.adminOnly === true && !isAdmin → 제외
   * - 그 외 → 유지
   * - widget이 adminOnly 필드를 갖지 않으면 노출 (기본값 false 취급)
   *
   * @template T
   * @param {ReadonlyArray<T & {adminOnly?: boolean}>} widgets - 필터 대상 위젯 배열
   * @returns {T[]} 권한 통과 위젯 배열 (입력 배열은 변경하지 않음)
   */
  function filterByPermission(widgets) {
    if (!Array.isArray(widgets)) {
      return []
    }
    const admin = isAdmin.value
    return widgets.filter((widget) => {
      if (widget && widget.adminOnly === true && !admin) {
        return false
      }
      return true
    })
  }

  return {
    isAdmin,
    canCustomize,
    filterByPermission
  }
}

/**
 * 테스트/디버그 전용. 권한 코드 목록을 외부에서 주입할 때 사용.
 * 일반 코드에서는 사용하지 않는다.
 *
 * @returns {ReadonlyArray<string>}
 */
export function getAdminRoleCodes() {
  return ADMIN_ROLE_CODES
}
